import { useState, useEffect } from "react";
import { Datepicker, Button, Icon, Modal } from "@thetreep/ui";
import Select from "@components/common/Select";
import { useTranslation } from "react-i18next";
import { Status } from "@gen/travel/v1/common_pb";
import { formatStatus } from "@utils/status";
import { InputFilter } from "@components/common/InputFilter";
import { useModal } from "@hooks/useModal";

interface DateRangeEvent {
  readonly date: Date | undefined | null | Array<Date | undefined | null>;
}

interface FiltersValue {
  dates: Date[] | null;
  status: number[] | undefined;
  search: string | undefined;
}

const statusOptions = [
  Status.UNSPECIFIED,
  Status.PRE_BOOKED,
  Status.BOOKING_IN_PROGRESS,
  Status.UPCOMING,
  Status.IN_PROGRESS,
];

export default function FiltersBookings({
  onChange,
  isCompleted,
}: {
  onChange: (filters: FiltersValue) => void;
  isCompleted?: boolean;
}) {
  const { t } = useTranslation();
  const { isOpen, openModal, closeModal } = useModal();
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(id);
  }, []);
  const [dates, setDates] = useState<Date[]>([]);
  const [filters, setFilters] = useState<FiltersValue>({
    dates: null,
    status: undefined,
    search: undefined,
  });

  const [localFilters, setLocalFilters] = useState<FiltersValue>(filters);

  const [prevIsOpen, setPrevIsOpen] = useState(isOpen);
  const [prevFilters, setPrevFilters] = useState(filters);
  if (isOpen !== prevIsOpen || filters !== prevFilters) {
    setPrevIsOpen(isOpen);
    setPrevFilters(filters);
    if (isOpen) {
      setLocalFilters(filters);
    }
  }

  const updateFilters = (newFilters: Partial<FiltersValue>) => {
    const updated = { ...filters, ...newFilters };
    setFilters(updated);
  };

  const handleTempChange = (newFilters: Partial<FiltersValue>) => {
    const updated = { ...localFilters, ...newFilters };
    setLocalFilters(updated);
  };

  const applyFilters = () => {
    setFilters(localFilters);
    onChange(localFilters);
    closeModal();
  };

  const handleDateChange = (date: DateRangeEvent["date"]) => {
    if (!date) {
      if (dates.length > 0) {
        setDates([]);
        updateFilters({ dates: null });
        onChange({ ...filters, dates: null });
      }
      return;
    }
    const values = Array.isArray(date)
      ? date.filter((d): d is Date => d !== null && d !== undefined)
      : [date];
    setDates(values);
    if (
      values.length > 0 ||
      (values.length === 0 && filters.dates && filters.dates?.length > 0)
    ) {
      updateFilters({ dates: values });
      onChange({ ...filters, dates: values });
    }
  };

  const options = statusOptions.map((status) => {
    const formattedStatus = formatStatus(`STATUS_${Status[status]}`, t);
    return {
      label: formattedStatus.name,
      value: status,
    };
  });

  return (
    <>
      <div className="md:hidden">
        <Button
          size="mini"
          width="100%"
          kind="outline"
          startEnhancer={<Icon color="#0C419A" src="settings_6_line" />}
          onClick={openModal}
        >
          {t("results.sortAndFilter")}
        </Button>

        <Modal
          isOpen={isOpen}
          size="default"
          onClose={closeModal}
          onConfirm={applyFilters}
          content={{ type: "confirm", title: t("userBookings.sortAndFilter") }}
        >
          <div className="flex gap-2 flex-col relative z-[9999]">
            <div className="flex-1">
              <InputFilter
                value={localFilters.search || ""}
                onChange={(value) =>
                  handleTempChange({ search: value as string })
                }
                placeholder={t("userBookings.place")}
              />
            </div>
            {!isCompleted && (
              <div className="flex-1">
                <Select
                  options={options}
                  kind="filter"
                  multiple={true}
                  value={localFilters.status}
                  onChange={(value) =>
                    handleTempChange({ status: value as number[] })
                  }
                  placeholder={t("userBookings.status")}
                />
              </div>
            )}
            <div className="flex-1">
              <Datepicker
                value={dates}
                showCalendar
                kind="filter"
                overrides={{
                  Root: {
                    style: {
                      zIndex: "9999",
                    },
                  },
                }}
                onRangeChange={({ date }: DateRangeEvent) => {
                  if (!date) {
                    setDates([]);
                    handleTempChange({ dates: null });
                    return;
                  }
                  const values = Array.isArray(date)
                    ? date.filter(
                        (d): d is Date => d !== null && d !== undefined,
                      )
                    : [date];
                  setDates(values);
                  handleTempChange({ dates: values });
                }}
                range
                clearable
                showIcon
                placeholder={t("userBookings.dates")}
              />
            </div>
          </div>
        </Modal>
      </div>

      <div className="gap-2 hidden md:flex mt-8">
        <div className="flex-1 max-w-[300px]">
          <InputFilter
            value={filters.search || ""}
            onChange={(value) => {
              updateFilters({ search: value as string });
              onChange({ ...filters, search: value as string });
            }}
            placeholder={t("userBookings.place")}
          />
        </div>
        {!isCompleted && (
          <div className="flex-1 max-w-[300px]">
            <Select
              options={options}
              kind="filter"
              multiple={true}
              value={filters.status}
              onChange={(value) => {
                updateFilters({ status: value as number[] });
                onChange({ ...filters, status: value as number[] });
              }}
              placeholder={t("userBookings.status")}
            />
          </div>
        )}
        <div className="flex-1 max-w-[300px]">
          {mounted && (
            <Datepicker
              value={dates}
              showCalendar
              kind="filter"
              onRangeChange={({ date }: DateRangeEvent) =>
                handleDateChange(date)
              }
              range
              clearable
              showIcon
              placeholder={t("userBookings.dates")}
            />
          )}
        </div>
      </div>
    </>
  );
}
